import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * Alert Component (Preline UI)
 *
 * Inline alert/notification component with icon and message.
 * Supports different severity levels (info, success, warning, error).
 *
 * @param {Object} props
 * @param {'info'|'success'|'warning'|'error'} props.severity - Alert severity level
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.className - Additional CSS classes
 */
export function Alert({ severity = 'info', children, className = '' }) {
  const severityConfig = {
    info: {
      bg: 'bg-brand/10',
      border: 'border-brand',
      text: 'text-brand',
      icon: 'lucide:info',
      iconColor: 'text-brand',
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      icon: 'lucide:check-circle',
      iconColor: 'text-green-600',
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      icon: 'lucide:alert-triangle',
      iconColor: 'text-yellow-600',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: 'lucide:alert-circle',
      iconColor: 'text-red-600',
    },
  };

  const config = severityConfig[severity];

  return (
    <div
      className={`flex p-4 ${config.bg} border ${config.border} rounded-lg ${className}`}
      role="alert"
    >
      <Icon icon={config.icon} className={`shrink-0 size-5 mt-0.5 ${config.iconColor}`} />
      <div className={`ms-3 text-sm ${config.text}`}>{children}</div>
    </div>
  );
}

Alert.propTypes = {
  severity: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
